import React, { Suspense, useMemo, useCallback, useRef, useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { Canvas } from '@react-three/fiber'
import * as THREE from 'three'

import ConnectionSpheres from '@components/Spheres/ConnectionSpheres.js'
import PublicConnectionSpheres from '@components/Spheres/PublicConnectionSpheres.js'
import SphereWithEffects from '@components/Spheres/SphereWithEffects.js'
import LocalDashedConnectionLine from '@components/Spheres/LocalDashedConnectionLine.js'

import { setEntryById } from '@redux/reducers/currentEntryReducer'

import { CONNECTION_TYPES } from '@constants/connectionTypes'
import {
  SPHERE_TYPES,
  LOCAL_SPHERE_SIZES,
  LOCAL_EXPLORE_CONNECTION_DISTANCE_SCALE,
  LOCAL_EXPLORE_CONNECTION_SIZE_SCALE,
  LOCAL_EXPLORE_MAIN_NODE_Y_OFFSET,
  LOCAL_EXPLORE_CHILD_DISTANCE_SCALE,
  LOCAL_EXPLORE_PARENT_DISTANCE_SCALE,
  LOCAL_EXPLORE_SECOND_ORDER_DISTANCE,
  LOCAL_EXPLORE_SUB_LAYOUT_BUFFER_SCALE,
  LOCAL_EXPLORE_SIBLING_RADIAL_SPREAD,
  LOCAL_EXPLORE_EXTERNAL_RADIAL_SPREAD,
} from '@constants/spheres'

import { transformConnection } from '@utils/transformConnection'
import { transformBackendToFrontendConnectionType } from '@utils/connectionTypeHelpers'
import extractTextFromHTML from '@utils/extractTextFromHTML'
import calculateSpherePositions from '@utils/calculateSpherePositions'
import {
  applyLocalExploreFit,
  computeLocalExploreFitScale,
  getLocalExploreViewportHalfExtents,
} from '@utils/localExploreViewport'
import { applyRadialSpreadFromCenter, applyUniformSiblingRadialSpread } from '@utils/localExploreSiblingSpread'

import styles from './Explore.module.scss'

const {
  FRONTEND: { PARENT, EXTERNAL, CHILD, SIBLING },
} = CONNECTION_TYPES

const getConnectionDistanceScale = (connectionType) => {
  if (connectionType === CHILD) return LOCAL_EXPLORE_CHILD_DISTANCE_SCALE
  if (connectionType === PARENT) return LOCAL_EXPLORE_PARENT_DISTANCE_SCALE
  return 1
}

const getLocalNodeRenderKey = (conn, transformed) => {
  if (conn?.connection_type === EXTERNAL) return `external-${conn.id}`
  return transformed?.id != null ? String(transformed.id) : `conn-${conn?.id}`
}

function getScaledSphereSize(baseSize, wordCount) {
  if (!wordCount || wordCount <= 0) return baseSize
  const factor = Math.log10(wordCount + 1) / 5
  const delta = Math.min(Math.max(factor, -0.5), 0.2)
  return baseSize * (1 + delta)
}

const getRawConnectionEndPos = (pos, conn, { lineExtensionFactor, externalDistanceFactor }) => {
  const isExternal = conn.connection_type === EXTERNAL
  const connectionTypeScale = getConnectionDistanceScale(conn.connection_type)
  const extensionFactor =
    (isExternal ? externalDistanceFactor : lineExtensionFactor) *
    LOCAL_EXPLORE_CONNECTION_DISTANCE_SCALE *
    connectionTypeScale

  return new THREE.Vector3(...pos)
    .multiplyScalar(extensionFactor)
    .add(new THREE.Vector3(0, LOCAL_EXPLORE_MAIN_NODE_Y_OFFSET, 0))
}

const normalizeLocalConnection = (conn, entryId) => {
  const normalizedType = transformBackendToFrontendConnectionType(conn.connection_type, entryId, {
    entry_id: entryId,
    foreign_entry_id: conn.foreign_entry_id,
    primary_entry_id: conn.primary_entry_id,
  })

  if (normalizedType) {
    return { ...conn, connection_type: normalizedType }
  }

  if (conn.foreign_source && !conn.foreign_entry_id) {
    return { ...conn, connection_type: EXTERNAL }
  }

  return conn
}

const LocalNodeNetworkView = ({
  entryId,
  title,
  content,
  connections,
  nodeEntriesInfo = [],
  publicOwnerUserId = null,
  mainNodeGoesToEdit,
}) => {
  const history = useHistory()
  const dispatch = useDispatch()
  const wrapperRef = useRef(null)
  const [viewportSize, setViewportSize] = useState({ width: 1, height: 1 })

  useEffect(() => {
    const element = wrapperRef.current
    if (!element) return undefined

    let rafId = null
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      const nextWidth = Math.max(width, 1)
      const nextHeight = Math.max(height, 1)

      if (rafId != null) {
        return
      }

      rafId = requestAnimationFrame(() => {
        rafId = null
        setViewportSize((prev) => {
          if (prev.width === nextWidth && prev.height === nextHeight) {
            return prev
          }
          return { width: nextWidth, height: nextHeight }
        })
      })
    })

    observer.observe(element)
    return () => {
      if (rafId != null) {
        cancelAnimationFrame(rafId)
      }
      observer.disconnect()
    }
  }, [])

  const normalizedConnections = useMemo(
    () => (connections || []).map((conn) => normalizeLocalConnection(conn, entryId)),
    [connections, entryId]
  )

  const {
    positions,
    center,
    lineExtensionFactor,
    externalDistanceFactor,
    horizontalRotation,
    verticalRotation,
    subConnectionVerticalOffset,
    subConnectionHorizontalOffset,
  } = calculateSpherePositions(normalizedConnections, {
    PARENT,
    EXTERNAL,
    CHILD,
    SIBLING,
  })

  const mainNodePosition = useMemo(
    () => [center[0], center[1] + LOCAL_EXPLORE_MAIN_NODE_Y_OFFSET, center[2]],
    [center]
  )

  const uniqueConnections = useMemo(() => {
    const seenNodeKeys = new Set()
    return (normalizedConnections || []).filter((conn) => {
      const transformed = transformConnection(entryId, conn)
      const nodeKey = getLocalNodeRenderKey(conn, transformed)
      if (seenNodeKeys.has(nodeKey)) return false
      seenNodeKeys.add(nodeKey)
      return true
    })
  }, [normalizedConnections, entryId])

  const renderedFirstOrderNodeIds = useMemo(
    () =>
      uniqueConnections
        .filter((conn) => conn?.connection_type !== EXTERNAL)
        .map((conn) => transformConnection(entryId, conn)?.id)
        .filter((id) => id != null),
    [uniqueConnections, entryId]
  )

  const getFirstOrderSphereSize = useCallback(
    (conn) => {
      const transformed = transformConnection(entryId, conn)
      const nodeInfo = nodeEntriesInfo.find((n) => n.id === transformed.id)
      return (
        getScaledSphereSize(LOCAL_SPHERE_SIZES[SPHERE_TYPES.FIRST_ORDER_CONNECTION], nodeInfo?.wdWordCount) *
        LOCAL_EXPLORE_CONNECTION_SIZE_SCALE
      )
    },
    [entryId, nodeEntriesInfo]
  )

  const layoutFit = useMemo(() => {
    const aspect = viewportSize.width / viewportSize.height
    const { halfWidth, halfHeight } = getLocalExploreViewportHalfExtents(aspect)
    const layoutCenter = new THREE.Vector3(...mainNodePosition)
    const mainRadius = LOCAL_SPHERE_SIZES[SPHERE_TYPES.MAIN]
    const fitPoints = [{ position: layoutCenter.clone(), radius: mainRadius }]

    uniqueConnections.forEach((conn) => {
      const pos = positions[conn.id]
      if (!pos) return

      const rawEnd = getRawConnectionEndPos(pos, conn, { lineExtensionFactor, externalDistanceFactor })
      const sphereSize = getFirstOrderSphereSize(conn)
      const subBuffer = LOCAL_EXPLORE_SECOND_ORDER_DISTANCE * LOCAL_EXPLORE_SUB_LAYOUT_BUFFER_SCALE
      const siblingSpreadBuffer = conn.connection_type === SIBLING ? LOCAL_EXPLORE_SIBLING_RADIAL_SPREAD : 0
      const externalSpreadBuffer = conn.connection_type === EXTERNAL ? LOCAL_EXPLORE_EXTERNAL_RADIAL_SPREAD : 0

      fitPoints.push({
        position: rawEnd,
        radius: sphereSize + subBuffer + siblingSpreadBuffer + externalSpreadBuffer,
      })
    })

    const scale = computeLocalExploreFitScale(fitPoints, layoutCenter, halfWidth, halfHeight)
    return { scale, center: mainNodePosition }
  }, [
    viewportSize,
    mainNodePosition,
    uniqueConnections,
    positions,
    lineExtensionFactor,
    externalDistanceFactor,
    getFirstOrderSphereSize,
  ])

  const mainTexture = useMemo(() => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    canvas.width = 1024
    canvas.height = 512

    ctx.fillStyle = 'black'
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    if (content) {
      const text = extractTextFromHTML(content)
      ctx.fillStyle = 'silver'
      ctx.font = '12px Syncopate'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'

      const words = text.split(' ')
      let line = ''
      const maxWidth = canvas.width - 100
      const lines = []

      for (let i = 0; i < words.length; i++) {
        const testLine = line + words[i] + ' '
        const metrics = ctx.measureText(testLine)
        if (metrics.width > maxWidth && i > 0) {
          lines.push(line.trim())
          line = words[i] + ' '
        } else {
          line = testLine
        }
      }
      lines.push(line.trim())

      const lineHeight = 40
      let y = canvas.height / 2 - ((lines.length - 1) * lineHeight) / 2

      lines.forEach((l) => {
        ctx.fillText(l, canvas.width / 2, y)
        y += lineHeight
      })
    }

    if (title) {
      ctx.font = 'bold 28px Syncopate'
      ctx.fillStyle = 'white'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(title, canvas.width / 2, canvas.height / 2)
    }

    const tex = new THREE.CanvasTexture(canvas)
    tex.wrapS = THREE.ClampToEdgeWrapping
    tex.wrapT = THREE.ClampToEdgeWrapping
    return tex
  }, [content, title])

  const handleMainNodeClick = useCallback(() => {
    if (!entryId) return
    if (mainNodeGoesToEdit) {
      history.push(`/edit-node-entry?entryId=${entryId}`)
      return
    }
    if (publicOwnerUserId) {
      history.push(`/show-node-entry?userId=${publicOwnerUserId}&entryId=${entryId}`)
    }
  }, [entryId, history, mainNodeGoesToEdit, publicOwnerUserId])

  const handleConnectionSphereClick = useCallback(
    async (id, conn) => {
      if (conn.connection_type === EXTERNAL) {
        if (conn.foreign_source) {
          window.open(conn.foreign_source, '_blank', 'noopener,noreferrer')
        }
        return
      }
      if (publicOwnerUserId && id) {
        history.push(`/explore?userId=${publicOwnerUserId}&entryId=${id}`)
        return
      }
      await dispatch(setEntryById(id))
      history.replace({ pathname: '/explore', search: `?entryId=${id}` })
    },
    [dispatch, history, publicOwnerUserId]
  )

  const fitPosition = useCallback(
    (rawPosition) => applyLocalExploreFit(rawPosition, layoutFit.center, layoutFit.scale),
    [layoutFit]
  )

  const fittedMainPosition = useMemo(
    () => fitPosition(new THREE.Vector3(...mainNodePosition)).toArray(),
    [fitPosition, mainNodePosition]
  )

  const getConnectionEndPosition = useCallback(
    (conn, rawEnd) => {
      const fitted = fitPosition(rawEnd)

      if (conn.connection_type === EXTERNAL) {
        return applyRadialSpreadFromCenter({
          fittedPosition: fitted,
          layoutCenter: fittedMainPosition,
          spreadAmount: LOCAL_EXPLORE_EXTERNAL_RADIAL_SPREAD,
        })
      }

      return applyUniformSiblingRadialSpread({
        fittedPosition: fitted,
        layoutCenter: fittedMainPosition,
        spreadAmount: LOCAL_EXPLORE_SIBLING_RADIAL_SPREAD,
        connectionType: conn.connection_type,
      })
    },
    [fitPosition, fittedMainPosition]
  )

  return (
    <div ref={wrapperRef} className={styles.nodesWrapper}>
      <Canvas style={{ width: '100%', height: '100%' }} camera={{ position: [0, 0, 8], fov: 75 }}>
        <ambientLight />
        <directionalLight position={[5, 5, 5]} />

        <Suspense fallback={null}>
          <group>
            <SphereWithEffects
              id={entryId}
              pos={fittedMainPosition}
              title={title}
              size={LOCAL_SPHERE_SIZES[SPHERE_TYPES.MAIN]}
              mainTexture={mainTexture}
              onClick={handleMainNodeClick}
              rotation={[0, 4.7, 0]}
            />
          </group>

          <group>
            {uniqueConnections?.map((conn) => {
              const pos = positions[conn.id]
              if (!pos) return null

              const rawEnd = getRawConnectionEndPos(pos, conn, { lineExtensionFactor, externalDistanceFactor })
              const endPos = getConnectionEndPosition(conn, rawEnd)
              const startPos = new THREE.Vector3(...fittedMainPosition)
              const isExternal = conn.connection_type === EXTERNAL

              if (isExternal) {
                return <LocalDashedConnectionLine key={`line-${conn.id}`} start={startPos} end={endPos} />
              }

              const points = [startPos, endPos]
              const curve = new THREE.CatmullRomCurve3(points)
              const geometry = new THREE.TubeGeometry(curve, 8, 0.02, 4, false)

              return (
                <mesh key={`line-${conn.id}`} geometry={geometry} renderOrder={0}>
                  <meshBasicMaterial color="white" depthWrite={false} depthTest={false} />
                </mesh>
              )
            })}
          </group>

          <group>
            {uniqueConnections?.map((conn) => {
              const transformed = transformConnection(entryId, conn)
              const pos = positions[conn.id]
              const hRotation = horizontalRotation[conn.id]
              const vRotation = verticalRotation[conn.id]
              if (!pos) return null

              const rawEnd = getRawConnectionEndPos(pos, conn, { lineExtensionFactor, externalDistanceFactor })
              const endPos = getConnectionEndPosition(conn, rawEnd)
              const sphereSize = getFirstOrderSphereSize(conn)

              return (
                <React.Fragment key={conn.id}>
                  <SphereWithEffects
                    id={transformed.id}
                    pos={endPos}
                    title={transformed.title}
                    size={sphereSize}
                    conn={conn}
                    onClick={() => handleConnectionSphereClick(transformed.id, conn)}
                    rotation={[vRotation, hRotation, 0]}
                  />

                  {publicOwnerUserId ? (
                    <PublicConnectionSpheres
                      conn={conn}
                      connId={transformed.id}
                      userId={publicOwnerUserId}
                      position={endPos}
                      size={sphereSize}
                      handleConnectionSphereClick={handleConnectionSphereClick}
                      rotation={[vRotation, hRotation, 0]}
                      verticalOffset={subConnectionVerticalOffset[conn.id] || 0}
                      horizontalOffset={subConnectionHorizontalOffset[conn.id] || 0}
                      currentEntryId={entryId}
                      graphCenter={fittedMainPosition}
                      layoutScale={layoutFit.scale}
                      excludedNodeIds={[entryId, ...renderedFirstOrderNodeIds]}
                    />
                  ) : (
                    <ConnectionSpheres
                      conn={conn}
                      connId={transformed.id}
                      position={endPos}
                      size={sphereSize}
                      handleConnectionSphereClick={handleConnectionSphereClick}
                      rotation={[vRotation, hRotation, 0]}
                      verticalOffset={subConnectionVerticalOffset[conn.id] || 0}
                      horizontalOffset={subConnectionHorizontalOffset[conn.id] || 0}
                      graphCenter={fittedMainPosition}
                      layoutScale={layoutFit.scale}
                      excludedNodeIds={[entryId, ...renderedFirstOrderNodeIds]}
                    />
                  )}
                </React.Fragment>
              )
            })}
          </group>
        </Suspense>
      </Canvas>
    </div>
  )
}

export default LocalNodeNetworkView
